import { fontSelect } from '@/components/settings-page/platformSettings/templatesSettings/constants';
import { IFontStyle, ITemplateStylingSettings } from '@/components/settings-page/platformSettings/templatesSettings/types';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import { Image as PdfImage, Text, View } from '@react-pdf/renderer';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import { Root } from 'remark-parse/lib';
import { DefaultTheme } from 'styled-components/dist/types';
import { unified } from 'unified';
import { parseMarkdownHtmlImages } from '../utils';
import { tableStyles } from './style';

export const getTermFontWeightForPDF = (fontProperties: IFontStyle) => {
  if (!fontProperties) return 700;

  const currentFontWeights = fontSelect?.[fontProperties.fontFamily as keyof typeof fontSelect];

  const fontWeightIndex = currentFontWeights?.indexOf(fontProperties.fontWeight);
  if (fontWeightIndex === currentFontWeights?.length - 1) {
    return Number(currentFontWeights?.[fontWeightIndex]);
  }
  return Number(currentFontWeights?.[fontWeightIndex + 1]);
};

export const getOneColumnSectionFieldsToDisplay = (fields: IFieldEditor[], includeAiSuggested: boolean): IFieldEditor[] => {
  return fields.filter(
    (field) =>
      field.value ||
      (field.AI?.answers.length && includeAiSuggested && (field.value === null || field.value === field.AI?.answers[0])) ||
      field?.attachments?.length
  );
};

// Functions to render markdown content to PDF format
const extractText = (node: any): string => {
  if (node.type === 'text') return node.value;
  if (Array.isArray(node.children)) return node.children.map(extractText).join('');
  return '';
};

const renderNodeForPDF = (
  node: any,
  key: number = 0,
  templateStylingData: ITemplateStylingSettings,
  maxImageWidth: number = 450,
  theme: DefaultTheme
): JSX.Element | JSX.Element[] | null => {
  switch (node.type) {
    case 'root':
      return node.children.map((child: any, index: number) => renderNodeForPDF(child, index, templateStylingData, maxImageWidth, theme));

    case 'paragraph':
      return (
        <View key={key} style={{ marginBottom: 8 }}>
          {node.children.map((child: any, index: number) => renderNodeForPDF(child, index, templateStylingData, maxImageWidth, theme))}
        </View>
      );

    case 'text':
      return <Text key={key}>{node.value}</Text>;

    case 'strong':
      return (
        <Text key={key} style={{ fontWeight: 600 }}>
          {node.children.map((child: any, index: number) => renderNodeForPDF(child, index, templateStylingData, maxImageWidth, theme))}
        </Text>
      );

    // case 'emphasis':
    //   return (
    //     <Text key={key} style={{ fontStyle: 'italic' }}>
    //       {node.children.map((child: any, index: number) => renderNodeForPDF(child, index))}
    //     </Text>
    //   );

    case 'heading': {
      const headingStyles: Record<number, { fontSize: string }> = {
        1: { fontSize: '20px' },
        2: { fontSize: '16px' },
        3: { fontSize: '14px' },
        4: { fontSize: '12px' },
        5: { fontSize: '10px' },
        6: { fontSize: '8px' }
      };
      return (
        <Text
          key={key}
          style={{
            ...(headingStyles[node.depth] || {
              fontSize: '20px'
            }),
            paddingBottom: '1px',
            marginBottom: '15px',
            borderBottom: `2px solid ${theme.layer[2]}`,
            fontFamily: templateStylingData?.headerFontStyle?.fontFamily || 'Roboto Slab',
            fontWeight: Number(templateStylingData?.headerFontStyle?.fontWeight) || 400,
            color: templateStylingData?.headerFontStyle?.fontColor ? `#${templateStylingData?.headerFontStyle?.fontColor}` : theme.font.strong
          }}
        >
          {node.children.map((child: any, index: number) => renderNodeForPDF(child, index, templateStylingData, maxImageWidth, theme))}
        </Text>
      );
    }

    case 'list':
      return (
        <View key={key} style={{ marginBottom: 8, marginLeft: node.level ? node.level * 16 : 0 }}>
          {node.children.map((item: any, index: number) => {
            const bullet = node.ordered ? `${index + 1}. ` : '• ';
            const nestedLists = item.children.filter((child: any) => child.type === 'list');
            const itemTextChildren = item.children.filter((child: any) => child.type !== 'list');
            return (
              <View key={`${key}-${index}`} style={{ flexDirection: 'column', marginBottom: 4 }}>
                <View style={{ flexDirection: 'row' }}>
                  <Text style={{ marginRight: 8 }}>{bullet}</Text>
                  <Text>
                    {itemTextChildren.map((child: any, childIndex: number) =>
                      renderNodeForPDF(child, childIndex, templateStylingData, maxImageWidth, theme)
                    )}
                  </Text>
                </View>
                {nestedLists.map((nestedList: any, nestedIdx: number) =>
                  renderNodeForPDF({ ...nestedList, level: (node.level || 0) + 1 }, nestedIdx, templateStylingData, maxImageWidth, theme)
                )}
              </View>
            );
          })}
        </View>
      );

    case 'table': {
      const rows = node.children.map((row: any) => row.children.map((cell: any) => extractText(cell)));
      return <View key={key}>{renderPDFTable(rows, templateStylingData, theme)}</View>;
    }

    case 'html': {
      const images = parseMarkdownHtmlImages(node.value);

      if (images.length === 0) {
        return null;
      }

      return images.map((img, index) => {
        let width = img.width || maxImageWidth;
        let height = img.height;

        if (img.width && img.width > maxImageWidth) {
          const aspectRatio = img.height && img.width ? img.height / img.width : 1;
          width = maxImageWidth;
          height = img.height ? Math.round(maxImageWidth * aspectRatio) : undefined;
        }

        return (
          <PdfImage
            key={`${key}-img-${index}`}
            src={img.src}
            style={{
              width,
              ...(height ? { height } : {}),
              objectFit: 'scale-down',
              margin: 10
            }}
          />
        );
      });
    }

    default:
      if (Array.isArray(node.children)) {
        return node.children.map((child: any, index: number) => renderNodeForPDF(child, index, templateStylingData, maxImageWidth, theme));
      }
      return null;
  }
};

const renderPDFTable = (data: string[][], templateStylingData: ITemplateStylingSettings, theme: DefaultTheme): JSX.Element => {
  const [header, ...bodyRows] = data;

  return (
    <View style={tableStyles.table} wrap={false}>
      <View style={{ ...tableStyles.tableRow, color: theme.font.base, backgroundColor: templateStylingData?.accentColor || '#5A6771' }}>
        {header.map((cell, index) => (
          <Text
            key={index}
            style={[
              tableStyles.tableCell,
              { borderBottomWidth: 0, fontWeight: 600, borderColor: theme.border.base, color: theme.context.light },
              index !== header.length - 1 && { borderRightWidth: 0 }
            ]}
          >
            {cell}
          </Text>
        ))}
      </View>

      {bodyRows.map((row, rowIndex) => (
        <View style={tableStyles.tableRow} key={rowIndex}>
          {row.map((cell, cellIndex) => (
            <Text
              key={cellIndex}
              style={[
                tableStyles.tableCell,
                { borderColor: theme.border.base },
                cellIndex !== row.length - 1 && { borderRightWidth: 0 },
                rowIndex !== bodyRows.length - 1 && { borderBottomWidth: 0 }
              ]}
            >
              {cell || ''}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
};

export const convertMarkdownToReactPDF = (
  markdown: string,
  templateStylingData: ITemplateStylingSettings,
  maxImageWidth: number = 450,
  theme: DefaultTheme
): JSX.Element[] => {
  const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown) as Root;

  const rendered = renderNodeForPDF(tree, 0, templateStylingData, maxImageWidth, theme);
  return Array.isArray(rendered) ? rendered : [rendered];
};

export const splitTermsThreeColumns = (arr: IFieldEditor[]) => {
  const partSize = Math.ceil(arr.length / 3);
  const parts = [];

  for (let i = 0; i < arr.length; i += partSize) {
    parts.push(arr.slice(i, i + partSize));
  }

  return parts;
};
